import { useState, useCallback, useEffect } from 'react';
import { useGoals, calcGoal } from '../../hooks/useGoals';
import { useAccounts } from '../../hooks/useAccounts';
import { useCards } from '../../hooks/useCards';
import { useFinancialInsights } from '../../hooks/useFinancialInsights';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { GoalModal } from './components/GoalModal';
import { GoalCard } from './components/GoalCard';
import { GoalDetailModal } from './components/GoalDetailModal';
import { InsightsSection } from './components/InsightsSection';
import { ContributionModal } from './components/ContributionModal';
import type { ContributionParams } from './components/ContributionModal';
import type { Goal, GoalContribution } from '../../hooks/useGoals';

type ActiveTab = 'metas' | 'insights';

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface GoalLimitProgressProps {
  current: number;
  limit: number;
  isPaid: boolean;
  onUpgrade: () => void;
}

const GoalLimitProgress = ({ current, limit, isPaid, onUpgrade }: GoalLimitProgressProps) => {
  const pct = Math.min(100, Math.round((current / limit) * 100));
  const isAtLimit = current >= limit;
  const isNearLimit = !isAtLimit && pct >= 67;

  const barColor = isAtLimit
    ? 'bg-red-500'
    : isNearLimit
      ? 'bg-amber-400'
      : 'bg-emerald-500';

  const bgColor = isAtLimit
    ? 'bg-red-50 border-red-100'
    : isNearLimit
      ? 'bg-amber-50 border-amber-100'
      : 'bg-emerald-50/60 border-emerald-100';

  const textColor = isAtLimit
    ? 'text-red-600'
    : isNearLimit
      ? 'text-amber-600'
      : 'text-emerald-700';

  const iconColor = isAtLimit
    ? 'text-red-500'
    : isNearLimit
      ? 'text-amber-500'
      : 'text-emerald-500';

  if (isPaid) return null;

  return (    <div className={`flex items-center gap-4 px-4 py-3 rounded-2xl border ${bgColor}`}>
      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <i className={`${isAtLimit ? 'ri-lock-line' : 'ri-flag-line'} text-lg`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className={`text-xs font-600 ${textColor}`}>
            {isAtLimit
              ? `Limite de metas atingido — ${current} de ${limit} usadas`
              : `${current} de ${limit} metas usadas`}
          </p>
          <span className={`text-xs font-700 ${textColor}`}>{pct}%</span>
        </div>
        <div className="h-1.5 bg-black/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {isAtLimit && (
          <p className="text-xs text-red-500/80 mt-1">Exclua uma meta ou faça upgrade para criar mais.</p>
        )}
        {isNearLimit && !isAtLimit && (
          <p className={`text-xs ${textColor} opacity-70 mt-1`}>Você está próximo do limite do plano gratuito.</p>
        )}
      </div>
      {(isAtLimit || isNearLimit) && (
        <button
          onClick={onUpgrade}
          className={`shrink-0 text-xs font-600 px-3 py-1.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors ${
            isAtLimit
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-amber-400 hover:bg-amber-500 text-white'
          }`}
        >
          Fazer upgrade
        </button>
      )}
    </div>
  );
};

export default function MetasPage() {
  const { goals, loading, createGoal, updateGoal, deleteGoal, addContribution, removeContribution, fetchContributions } = useGoals();
  const { accounts } = useAccounts();
  const { cards } = useCards();
  const { checkGoalLimit, isPaid } = usePlanLimits();
  const limitCheck = checkGoalLimit();

  const [tab, setTab] = useState<ActiveTab>('metas');
  const [showModal, setShowModal] = useState(false);
  const [editGoal, setEditGoal] = useState<Goal | null>(null);
  const [detailGoal, setDetailGoal] = useState<Goal | null>(null);
  const [contribGoal, setContribGoal] = useState<Goal | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Sync detailGoal with latest data after any update (e.g., contribution removed)
  useEffect(() => {
    if (detailGoal) {
      const updated = goals.find(g => g.id === detailGoal.id);
      if (updated) setDetailGoal(updated);
    }
  // detailGoal.id é estável durante a vida do modal; goals é o gatilho real.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals]);

  const {
    insights, loading: insightsLoading,
    monthIncome, monthExpenses, prevMonthExpenses, savingsRate,
  } = useFinancialInsights(goals);

  const activeGoals = goals.filter(g => g.status === 'active' && !g.is_completed && g.current_amount < g.target_amount);
  const pausedGoals = goals.filter(g => g.status === 'paused');
  const completedGoals = goals.filter(g => g.status === 'completed' || g.is_completed || g.current_amount >= g.target_amount);
  const totalAccumulated = goals.reduce((s, g) => s + Number(g.current_amount), 0);

  // Enriched stats
  const allCalcs = goals.map(g => ({ goal: g, calc: calcGoal(g) }));
  const totalRemaining = allCalcs
    .filter(({ goal: g }) => g.status === 'active' && !g.is_completed)
    .reduce((s, { calc }) => s + calc.remaining, 0);
  const atRiskCount = allCalcs.filter(({ calc }) =>
    calc.pace === 'at_risk' || calc.pace === 'behind' || calc.pace === 'overdue'
  ).length;
  const nextDeadlineGoal = goals
    .filter(g => g.target_date && g.status === 'active' && !g.is_completed)
    .sort((a, b) => new Date(a.target_date!).getTime() - new Date(b.target_date!).getTime())[0] ?? null;

  const nextDeadlineLabel = nextDeadlineGoal?.target_date
    ? (() => {
        const today = new Date();
        const target = new Date(nextDeadlineGoal.target_date + 'T12:00:00');
        const months = Math.round(
          (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth())
        );
        return months <= 0 ? 'Venceu' : months === 1 ? '1 mês' : `${months} meses`;
      })()
    : null;

  const handleNewGoal = () => {
    if (!limitCheck.allowed) { setShowUpgrade(true); return; }
    setEditGoal(null);
    setShowModal(true);
  };

  const handleSave = async (values: Omit<Goal, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (editGoal) return updateGoal(editGoal.id, values);
    return createGoal(values);
  };

  const handleDelete = async (id: string) => {
    await deleteGoal(id);
    setDeleteConfirm(null);
  };

  const handleContribution = async (params: ContributionParams) => {
    if (!contribGoal) return { error: 'Nenhuma meta selecionada' };
    return addContribution(
      contribGoal.id,
      params.amount,
      params.note,
      contribGoal.current_amount,
      {
        sourceType: params.sourceType,
        sourceAccountId: params.sourceAccountId,
        sourceCardId: params.sourceCardId,
        linkedAccountId: params.linkedAccountId,
        contributionDate: params.contributionDate,
        observation: params.observation,
      }
    );
  };

  const handleRemoveContribution = useCallback(
    (contribution: GoalContribution) => removeContribution(contribution),
    [removeContribution]
  );

  const fetchContribsCb = useCallback(
    (goalId: string): Promise<GoalContribution[]> => fetchContributions(goalId),
    [fetchContributions]
  );

  const handleGoalClickFromInsights = (goalId: string) => {
    const g = goals.find(g => g.id === goalId);
    if (g) { setTab('metas'); setDetailGoal(g); }
  };

  const detailCalc = detailGoal ? calcGoal(detailGoal) : null;

  const STAT_CARDS = [
    {
      label: 'Total acumulado',
      value: fmt(totalAccumulated),
      sub: `em ${goals.length} ${goals.length === 1 ? 'meta' : 'metas'}`,
      icon: 'ri-money-dollar-circle-line',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Ainda faltam',
      value: fmt(totalRemaining),
      sub: `para ${activeGoals.length} ${activeGoals.length === 1 ? 'meta ativa' : 'metas ativas'}`,
      icon: 'ri-target-line',
      color: totalRemaining > 0 ? 'text-gray-700' : 'text-emerald-600',
      bg: 'bg-gray-50',
    },
    {
      label: 'Precisam de atenção',
      value: String(atRiskCount),
      sub: atRiskCount === 0 ? 'Tudo em ordem' : `${atRiskCount} ${atRiskCount === 1 ? 'meta em risco' : 'metas em risco'}`,
      icon: atRiskCount > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line',
      color: atRiskCount > 0 ? 'text-amber-600' : 'text-emerald-600',
      bg: atRiskCount > 0 ? 'bg-amber-50' : 'bg-emerald-50',
    },
    {
      label: 'Prazo mais próximo',
      value: nextDeadlineGoal ? nextDeadlineLabel ?? '—' : '—',
      sub: nextDeadlineGoal ? nextDeadlineGoal.name : 'Nenhuma meta com prazo',
      icon: 'ri-calendar-event-line',
      color: 'text-gray-700',
      bg: 'bg-gray-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Metas Financeiras</h1>
          <p className="text-sm text-gray-500 mt-0.5">Defina objetivos, acompanhe o progresso e receba insights</p>
        </div>
        <button
          onClick={handleNewGoal}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Nova Meta
          {!isPaid && limitCheck.limit !== null && (
            <span className="ml-1 text-emerald-200 text-xs">({limitCheck.current}/{limitCheck.limit})</span>
          )}
        </button>
      </div>

      {limitCheck.limit !== null && (
        <GoalLimitProgress
          current={limitCheck.current}
          limit={limitCheck.limit}
          isPaid={isPaid}
          onUpgrade={() => setShowUpgrade(true)}
        />
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <i className={`${s.icon} text-sm ${s.color}`} />
              </div>
              <p className="text-xs text-gray-500 font-500">{s.label}</p>
            </div>
            <p className={`text-base font-700 ${s.color}`}>{s.value}</p>
            {s.sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {([['metas', 'ri-focus-3-line', 'Minhas Metas'], ['insights', 'ri-bar-chart-line', 'Insights Financeiros']] as const).map(([t, icon, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all cursor-pointer whitespace-nowrap ${
              tab === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${icon} text-sm`} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'metas' && (
        <>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => <div key={i} className="h-44 bg-gray-100 rounded-2xl animate-pulse" />)}
            </div>
          ) : goals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-5">
                <i className="ri-focus-3-line text-3xl text-emerald-400" />
              </div>
              <h2 className="text-lg font-700 text-gray-800 mb-2">Sua jornada começa aqui</h2>
              <p className="text-sm text-gray-500 mb-2 max-w-sm leading-relaxed">
                Definir uma meta é o primeiro passo. Pode começar pequeno — o que importa é ter um destino claro e acompanhar o progresso.
              </p>
              <p className="text-xs text-gray-400 mb-6 max-w-xs">
                Você receberá cálculos automáticos, sugestões de aporte mensal e alertas quando uma meta precisar de atenção.
              </p>
              <div className="flex items-center gap-2 mb-7 flex-wrap justify-center">
                {[
                  { icon: 'ri-bar-chart-line', label: 'Progresso visual' },
                  { icon: 'ri-calculator-line', label: 'Cálculos automáticos' },
                  { icon: 'ri-lightbulb-line', label: 'Insights personalizados' },
                ].map(chip => (
                  <span key={chip.label} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-600">
                    <i className={`${chip.icon} text-xs`} />
                    {chip.label}
                  </span>
                ))}
              </div>
              <button
                onClick={handleNewGoal}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-add-line" />Criar minha primeira meta
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {activeGoals.length > 0 && (
                <div>
                  <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-3">Em andamento</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeGoals.map(goal => (
                      <GoalCard
                        key={goal.id}
                        goal={goal}
                        calc={calcGoal(goal)}
                        onView={() => setDetailGoal(goal)}
                        onEdit={() => { setEditGoal(goal); setShowModal(true); }}
                        onAddContribution={() => setContribGoal(goal)}
                        onDelete={() => setDeleteConfirm(goal.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {pausedGoals.length > 0 && (
                <div>
                  <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-3">Pausadas</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pausedGoals.map(goal => (
                      <div key={goal.id} className="opacity-70 hover:opacity-100 transition-opacity">
                        <GoalCard
                          goal={goal}
                          calc={calcGoal(goal)}
                          onView={() => setDetailGoal(goal)}
                          onEdit={() => { setEditGoal(goal); setShowModal(true); }}
                          onAddContribution={() => setContribGoal(goal)}
                          onDelete={() => setDeleteConfirm(goal.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {completedGoals.length > 0 && (
                <div>
                  <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-3">Concluídas</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {completedGoals.map(goal => (
                      <GoalCard
                        key={goal.id}
                        goal={goal}
                        calc={calcGoal(goal)}
                        onView={() => setDetailGoal(goal)}
                        onEdit={() => { setEditGoal(goal); setShowModal(true); }}
                        onAddContribution={() => setContribGoal(goal)}
                        onDelete={() => setDeleteConfirm(goal.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'insights' && (
        <InsightsSection
          insights={insights}
          loading={insightsLoading}
          monthIncome={monthIncome}
          monthExpenses={monthExpenses}
          prevMonthExpenses={prevMonthExpenses}
          savingsRate={savingsRate}
          goals={goals}
          onGoalClick={handleGoalClickFromInsights}
        />
      )}

      {/* Modals */}
      <GoalModal
        open={showModal}
        goal={editGoal}
        onClose={() => { setShowModal(false); setEditGoal(null); }}
        onSave={handleSave}
      />

      <GoalDetailModal
        open={!!detailGoal}
        goal={detailGoal}
        calc={detailCalc}
        onClose={() => setDetailGoal(null)}
        onEdit={g => { setDetailGoal(null); setEditGoal(g); setShowModal(true); }}
        onAddContribution={g => { setDetailGoal(null); setContribGoal(g); }}
        fetchContributions={fetchContribsCb}
        removeContribution={handleRemoveContribution}
      />

      {contribGoal && (
        <ContributionModal
          open={!!contribGoal}
          goal={contribGoal}
          accounts={accounts}
          cards={cards}
          onClose={() => setContribGoal(null)}
          onAdd={handleContribution}
        />
      )}

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Limite de metas atingido"
        description={`O plano Free permite apenas ${limitCheck.limit} metas. Faça upgrade para criar metas ilimitadas.`}
      />

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="text-base font-700 text-gray-900 mb-2">Excluir meta?</h3>
            <p className="text-sm text-gray-500 mb-5">Essa ação é irreversível. Todo o progresso e histórico de aportes será perdido.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
